<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-slate-50 dark:bg-slate-900"
  >
    <!-- Sidebar -->
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />


    <!-- Main content -->
    <div class="flex flex-col flex-1 min-w-0 w-full">
      <!-- Top bar -->
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <!-- Content area -->
      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="max-w-6xl mx-auto">
          <!-- Page header -->
          <div
            class="flex flex-col justify-between gap-3 mb-4 sm:flex-row sm:items-center"
          >
            <div>
              <h1 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ t("account_settings_title") }}
              </h1>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ t("account_settings_subtitle") }}
              </p>
            </div>
          </div>

          <!-- Card wrapper (tabs + form) -->
          <section
            class="overflow-hidden bg-white border shadow-sm dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
          >
            <!-- Tabs -->
            <header
              class="flex items-center justify-between px-4 pt-4 pb-2 border-b border-slate-100 dark:border-slate-800"
            >
              <div
                class="inline-flex items-center gap-2 p-1 rounded-full bg-slate-50 dark:bg-slate-800"
              >
                <!-- Account tab -->
                <button
                  type="button"
                  @click="activeTab = 'account'"
                  class="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium rounded-full transition shadow-sm"
                  :class="
                    activeTab === 'account'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/80 dark:hover:bg-slate-700/60'
                  "
                >
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 text-[13px] rounded-full"
                    :class="
                      activeTab === 'account'
                        ? 'bg-secondary/50 text-white'
                        : 'bg-secondary/10 text-primary'
                    "
                  >
                    <i class="mdi mdi-account-outline"></i>
                  </span>
                  <span>{{ t("account_tab_account") }}</span>
                </button>

                <!-- Security tab -->
                <button
                  type="button"
                  @click="activeTab = 'security'"
                  class="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium rounded-full transition"
                  :class="
                    activeTab === 'security'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/80 dark:hover:bg-slate-700/60'
                  "
                >
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 text-[13px] rounded-full"
                    :class="
                      activeTab === 'security'
                        ? 'bg-secondary/50 text-white'
                        : 'bg-secondary/10 text-primary'
                    "
                  >
                    <i class="mdi mdi-lock-outline"></i>
                  </span>
                  <span>{{ t("account_tab_security") }}</span>
                </button>
              </div>

              <span class="hidden text-xs text-slate-400 sm:inline">
                {{ t("account_last_updated_hint") }}
              </span>
            </header>

            <!-- ACCOUNT FORM  -->
            <form
              v-if="activeTab === 'account'"
              class="space-y-6"
              @submit.prevent="saveSettings"
            >
              <!-- Client section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-briefcase-outline text-secondary"></i>
                  <span>{{ t("account_section_client") }}</span>
                  <span class="text-rose-500">*</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div
                    class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
                  >
                    <!-- Client name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_client_name") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 rounded-l-md bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-l-none rounded-r-md'
                              : 'left-0'
                          "
                        >
                          <i class="mdi mdi-account-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.client.name"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_client_name_placeholder')
                          "
                        />
                        <p
                          v-if="errors?.client?.name"
                          class="mt-1 text-xs text-red-500"
                        >
                          {{ errors.client.name[0] }}
                        </p>
                      </div>
                    </div>

                    <!-- Activity -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_activity") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-pulse text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.client.activity"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('account_field_activity_placeholder')"
                        />
                      </div>
                    </div>

                    <!-- Manager name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_manager_name") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-account-tie-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.client.manager"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_manager_name_placeholder')
                          "
                        />
                      </div>
                    </div>

                  
                    <!-- ✅ Activity Description (Client only) -->
                    <div v-if="isClient" class="lg:col-span-3 md:col-span-2">
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{
                          t("account_field_activity_description") ||
                          "Activity Description"
                        }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute top-2.5 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                          style="height: 32px"
                        >
                          <i class="mdi mdi-text-long text-[18px]"></i>
                        </span>

                        <textarea
                          v-model="form.client.activity_description"
                          rows="4"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_activity_description') || ''
                          "
                        ></textarea>
                      </div>

                      <p
                        v-if="errors?.client?.activity_description"
                        class="mt-1 text-xs text-red-500"
                      >
                        {{ errors.client.activity_description[0] }}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Address section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-map-marker-outline text-secondary"></i>
                  <span>{{ t("account_section_address") }}</span>
                  <span class="text-rose-500">*</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <!-- First row -->
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- City -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_city") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-city-variant-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.address.city"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('account_field_city_placeholder')"
                        />
                      </div>
                    </div>

                    <!-- District -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_district") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-map-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.district"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('account_field_district_placeholder')"
                        />
                      </div>
                    </div>

                    <!-- Street -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_street") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-sign-direction text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.street"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('account_field_street_placeholder')"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- Second row -->
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- Building number -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_building_number") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-office-building-marker-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.address.building"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_building_number_placeholder')
                          "
                        />
                      </div>
                    </div>

                    <!-- Additional number -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_additional_number") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-pound text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.additional"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_additional_number_placeholder')
                          "
                        />
                      </div>
                    </div>

                    <!-- Postal code -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_postal_code") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-mailbox-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.postalCode"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_postal_code_placeholder')
                          "
                        />
                      </div>
                    </div>
                  </div>

                  <!-- Short address -->
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ t("account_field_short_address") }}
                    </label>
                    <div class="relative">
                      <span
                        class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                        :class="
                          lang === 'ar'
                            ? 'right-0 rounded-r-md'
                            : 'left-0 rounded-l-md'
                        "
                      >
                        <i class="mdi mdi-home-map-marker text-[18px]"></i>
                      </span>
                      <input
                        v-model="form.address.shortAddress"
                        type="text"
                        class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                        :placeholder="
                          t('account_field_short_address_placeholder')
                        "
                      />
                    </div>
                  </div>
                </div>
              </section>

              <!-- Bank section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-bank-outline text-secondary"></i>
                  <span>{{ t("account_section_bank") }}</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- Account holder name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_holder_name") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-account-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.bank.holder"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_holder_name_placeholder')
                          "
                        />
                      </div>
                    </div>

                    <!-- Account / IBAN -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_iban") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-credit-card-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.bank.iban"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('account_field_iban_placeholder')"
                        />
                      </div>
                    </div>

                    <!-- Bank name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("account_field_bank_name") }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-bank text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.bank.name"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="
                            t('account_field_bank_name_placeholder')
                          "
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              
              <!-- Actions -->
              <footer
                class="flex flex-col items-stretch gap-3 px-5 py-4 border-t border-slate-100 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60"
              >
                <div class="flex gap-2">
                  <button
                    type="submit"
                    :disabled="saving"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i v-if="saving" class="mdi mdi-loading mdi-spin me-2"></i>
                    {{
                      saving ? t("account_btn_saving") : t("account_btn_save")
                    }}
                  </button>
                  <button
                    type="button"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium bg-white border rounded-lg border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700"
                  >
                    {{ t("account_btn_cancel") }}
                  </button>
                </div>

                <p class="text-[11px] text-slate-400">
                  {{ t("account_required_fields_note") }}
                </p>
              </footer>
            </form>

            <!-- SECURITY FORM -->
            <form v-else class="space-y-6" @submit.prevent="savePassword">
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-lock-outline text-secondary"></i>
                  <span>{{ t("security_section_title") }}</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <!-- Current password -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("security_current_password") }}
                      </label>

                      <div class="relative">
                        <!-- Left/Right lock icon -->
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-lock-outline text-[18px]"></i>
                        </span>

                        <!-- Eye icon -->
                        <button
                          type="button"
                          class="absolute inset-y-0 flex items-center justify-center w-8 text-slate-500 hover:text-primary"
                          :class="lang === 'ar' ? 'left-0' : 'right-0'"
                          @click="togglePasswordVisibility('current')"
                        >
                          <i
                            :class="
                              passwordVisible.current
                                ? 'mdi mdi-eye-off-outline'
                                : 'mdi mdi-eye-outline'
                            "
                            class="text-[18px]"
                          ></i>
                        </button>

                        <input
                          v-model="passwordForm.current_password"
                          :type="passwordVisible.current ? 'text' : 'password'"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-9' : 'pl-9 pr-9'"
                          :placeholder="
                            t('security_current_password_placeholder')
                          "
                        />
                      </div>

                      <p
                        v-if="passwordErrors.current_password"
                        class="mt-1 text-xs text-red-500"
                      >
                        {{ passwordErrors.current_password[0] }}
                      </p>
                    </div>

                    <!-- spacer to keep next row aligned -->
                    <div class="hidden md:block"></div>

                    <!-- New password -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("security_new_password") }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-key-outline text-[18px]"></i>
                        </span>

                        <button
                          type="button"
                          class="absolute inset-y-0 flex items-center justify-center w-8 text-slate-500 hover:text-primary"
                          :class="lang === 'ar' ? 'left-0' : 'right-0'"
                          @click="togglePasswordVisibility('new')"
                        >
                          <i
                            :class="
                              passwordVisible.new
                                ? 'mdi mdi-eye-off-outline'
                                : 'mdi mdi-eye-outline'
                            "
                            class="text-[18px]"
                          ></i>
                        </button>

                        <input
                          v-model="passwordForm.password"
                          :type="passwordVisible.new ? 'text' : 'password'"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-9' : 'pl-9 pr-9'"
                          :placeholder="t('security_new_password_placeholder')"
                        />
                      </div>

                      <p
                        v-if="passwordErrors.password"
                        class="mt-1 text-xs text-red-500"
                      >
                        {{ passwordErrors.password[0] }}
                      </p>
                    </div>

                    <!-- Confirm new password -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("security_confirm_password") }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-key-change text-[18px]"></i>
                        </span>

                        <button
                          type="button"
                          class="absolute inset-y-0 flex items-center justify-center w-8 text-slate-500 hover:text-primary"
                          :class="lang === 'ar' ? 'left-0' : 'right-0'"
                          @click="togglePasswordVisibility('confirm')"
                        >
                          <i
                            :class="
                              passwordVisible.confirm
                                ? 'mdi mdi-eye-off-outline'
                                : 'mdi mdi-eye-outline'
                            "
                            class="text-[18px]"
                          ></i>
                        </button>

                        <input
                          v-model="passwordForm.password_confirmation"
                          :type="passwordVisible.confirm ? 'text' : 'password'"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-9' : 'pl-9 pr-9'"
                          :placeholder="
                            t('security_confirm_password_placeholder')
                          "
                        />
                      </div>

                      <p
                        v-if="passwordErrors.password_confirmation"
                        class="mt-1 text-xs text-red-500"
                      >
                        {{ passwordErrors.password_confirmation[0] }}
                      </p>
                    </div>

                    <!-- Password Requirements (full width) -->
                    <div class="md:col-span-2">
                      <div
                        class="p-3 mt-1 border rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60"
                      >
                        <p
                          class="text-[12px] font-medium text-slate-700 dark:text-slate-200 mb-1"
                        >
                          {{ t("security_password_requirements_title") }}
                        </p>
                        <ul
                          class="text-[12px] leading-5 text-slate-500 dark:text-slate-400 list-disc ps-5 space-y-0.5"
                        >
                          <li>{{ t("security_password_req_len") }}</li>
                          <li>{{ t("security_password_req_lower") }}</li>
                          <li>
                            {{ t("security_password_req_number_symbol") }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Actions -->
              <footer
                class="flex flex-col items-stretch gap-3 px-5 py-4 border-t border-slate-100 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60"
              >
                <div class="flex gap-2">
                  <button
                    type="submit"
                    :disabled="passwordSaving"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i
                      v-if="passwordSaving"
                      class="mdi mdi-loading mdi-spin me-2"
                    ></i>
                    {{
                      passwordSaving
                        ? t("security_btn_saving")
                        : t("security_btn_save")
                    }}
                  </button>

                  <button
                    type="button"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium bg-white border rounded-lg border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700"
                    @click="
                      passwordForm.current_password = '';
                      passwordForm.password = '';
                      passwordForm.password_confirmation = '';
                      passwordErrors = {
                        current_password: {},
                        password: {},
                        password_confirmation: {},
                      };
                      passwordVisible = {
                        current: false,
                        new: false,
                        confirm: false,
                      };
                    "
                  >
                    {{ t("security_btn_rest") }}
                  </button>
                </div>

                <p class="text-[11px] text-slate-400">
                  {{ t("security_password_hint") }}
                </p>
              </footer>
            </form>
          </section>
        </div>
        <Toast
          :show="toast.show"
          :type="toast.type"
          :message="toast.message"
          @update:show="toast.show = $event"
        />
      </main>
    </div>
  </div>
</template>

<script>
import { messages } from "@/i18n";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import Toast from "@/components/Toast.vue";
import api from "@/services/api";
import { fileToPayload } from "@/services/files";
import { translateCookieToastMessage } from "@/utils/cookieErrors";

export default {
  name: "AdminConsultingDashboard",
  components: { AdminSidebar, AdminTopbar, Toast },

  data() {
    const initial = (
      document.documentElement.lang ||
      localStorage.getItem("app_lang") ||
      "ar"
    ).toLowerCase();

    return {
      messages,
      lang: initial.startsWith("ar") ? "ar" : "en",

      _langObserver: null,
      _langHandler: null,
      _toastTimer: null,

      isSidebarCollapsed: false,
      isSidebarOpenMobile: false,
      sidebarSearch: "",

      saving: false,
      loading: true,
      activeTab: "account",

      passwordSaving: false,

      passwordForm: {
        current_password: "",
        password: "",
        password_confirmation: "",
      },

      passwordErrors: {
        current_password: {},
        password: {},
        password_confirmation: {},
      },

      passwordVisible: {
        current: false,
        new: false,
        confirm: false,
      },

      toast: {
        show: false,
        type: "success",
        message: "",
      },

      errors: {
        client: {},
        address: {},
        bank: {},
      },

      user: null,
      clientId: null,

      // ✅ same idea as old template
      hydratedOnce: false,

      form: {
        client: {
          name: "",
          activity: "",
          manager: "",
          activity_description: "",
        },
        address: {
          city: "",
          district: "",
          street: "",
          building: "",
          additional: "",
          postalCode: "",
          shortAddress: "",
        },
        bank: { holder: "", iban: "", name: "" },

        // ✅ attachments array (files)
        attachments: [],
      },
    };
  },
  computed: {
    isConsultant() {
      const role = (this.user?.role || this.user?.type || "").toLowerCase();
      return role.includes("consultant") || role.includes("مستشار");
    },
    isClient() {
      return !this.isConsultant;
    },
  },

  async mounted() {
    this.syncLangListeners();
    await this.initUserFromApi();
  },

  beforeUnmount() {
    if (this._langObserver) this._langObserver.disconnect();
    if (this._langHandler)
      window.removeEventListener("app:lang", this._langHandler);
    if (this._toastTimer) clearTimeout(this._toastTimer);
  },

  methods: {
    t(key) {
      return (this.messages[this.lang] && this.messages[this.lang][key]) || key;
    },

    togglePasswordVisibility(field) {
      this.passwordVisible[field] = !this.passwordVisible[field];
    },

    toggleSidebar() {
      if (window.innerWidth < 768) {
        this.isSidebarOpenMobile = !this.isSidebarOpenMobile;
      } else {
        this.isSidebarCollapsed = !this.isSidebarCollapsed;
      }
    },

    // ---------------------------
    // ✅ GET USER/CLIENT FROM API
    // ---------------------------
    async initUserFromApi() {
      this.loading = true;

      try {
        // 1) جرّب endpoint /my
        let meRes = null;
        try {
          meRes = await api.get("/my", {
            headers: { Accept: "application/json" },
          });
        } catch (_) {
          meRes = null;
        }

        if (meRes?.data) {
          const me = meRes.data?.data ?? meRes.data;

          // ✅ إذا مستشار
          if (
            me?.consultant ||
            me?.role?.toLowerCase?.().includes("consultant")
          ) {
            const consultant = me.consultant ?? me;
            this.setConsultantUser(consultant);
            this.fillFormFromUser();
            return;
          }

          // ✅ إذا عميل
          if (me?.client) {
            const client = me.client;
            this.clientId = client.id ?? me.client_id ?? null;
            this.setClientUser(client);
            this.fillFormFromUser();
            return;
          }
        }

        // 2) fallback: من الكوكي
        const raw = this.getCookie("auth_user");
        if (!raw) throw new Error("auth_user cookie missing");

        let parsed = JSON.parse(raw);
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
        const root = parsed?.data ?? parsed;
        const cookieUser = root?.user ?? root;

        const role = (cookieUser?.role || cookieUser?.type || "").toLowerCase();

        // ✅ Consultant from cookie
        if (role.includes("consultant") || role.includes("مستشار")) {
          const consultantId =
            cookieUser?.consultant?.id || cookieUser?.consultant_id;

          if (!consultantId) {
            // لو الكوكي فيه بيانات المستشار كاملة
            this.setConsultantUser(cookieUser.consultant ?? cookieUser);
            this.fillFormFromUser();
            return;
          }

          const { data: fresh } = await api.get(
            `/consultants/${consultantId}`,
            { headers: { Accept: "application/json" } }
          );

          const consultant = fresh?.data ?? fresh;
          this.setConsultantUser(consultant);
          this.fillFormFromUser();
          return;
        }

        // ✅ Client from cookie
        this.clientId = cookieUser?.client?.id || cookieUser?.client_id || null;

        if (!this.clientId) throw new Error("Client ID not found.");

        const { data: fresh } = await api.get(`/clients/${this.clientId}`, {
          headers: { Accept: "application/json" },
        });

        const client = fresh?.data ?? fresh;
        this.setClientUser(client);
        this.fillFormFromUser();
      } catch (err) {
        console.error("initUserFromApi error:", err);
        const msg = translateCookieToastMessage(err?.message, this.t.bind(this));
        this.showToast(msg || this.t("account_saved_failed"), "error");
      } finally {
        this.loading = false;
      }
    },

    setClientUser(client) {
      this.user = {
        role: "client",
        client,
        address: client.address ?? null,
        bank_account: client.bank_account ?? client.bank ?? null,
      };
    },

    setConsultantUser(consultant) {
      this.user = {
        role: "consultant",
        consultant,
        address: consultant.address ?? null,
        bank_account: consultant.bank_account ?? consultant.bank ?? null,
      };
    },

    // ---------------------------
    // ✅ SAVE (CONSULTANT = multipart incl. attachments)
    // ---------------------------
    async saveSettings() {
      if (this.loading) return;
      if (this.isClient && !this.clientId) return;

      this.saving = true;

      // ✅ reset old errors
      this.errors = { client: {}, address: {}, bank: {} };

      const url = this.isConsultant ? `/consultants` : `/clients`;

      try {
        console.log("➡️ UPDATE method: POST");
        console.log("➡️ UPDATE url:", url);

        let res = null;

        if (this.isConsultant) {
          // ✅ multipart/form-data for consultant
          const fd = new FormData();

          fd.append("consultant[name]", this.form.client.name || "");
          fd.append(
            "consultant[specialization]",
            this.form.client.activity || ""
          );
          fd.append(
            "consultant[license_number]",
            this.form.client.manager || ""
          );

          fd.append("address[city]", this.form.address.city || "");
          fd.append("address[district]", this.form.address.district || "");
          fd.append("address[street]", this.form.address.street || "");
          fd.append(
            "address[building_number]",
            this.form.address.building || ""
          );
          fd.append(
            "address[additional_number]",
            this.form.address.additional || ""
          );
          fd.append("address[postal_code]", this.form.address.postalCode || "");
          fd.append(
            "address[short_address]",
            this.form.address.shortAddress || ""
          );

          fd.append("bank[account_holder_name]", this.form.bank.holder || "");
          fd.append("bank[account_number]", this.form.bank.iban || "");
          fd.append("bank[bank_name]", this.form.bank.name || "");

          // attachments[]
          (this.form.attachments || []).forEach((file, i) => {
            fd.append(`attachments[${i}]`, file);
          });

          console.log("➡️ UPDATE payload: FormData (consultant)");

          res = await api.post(url, fd, {
            headers: {
              Accept: "application/json",
              "Content-Type": "multipart/form-data",
            },
          });
        } else {
          // ✅ normal JSON for client
          const payload = {
            client: {
              name: this.form.client.name,
              activity: this.form.client.activity,
              manager_name: this.form.client.manager,
              activity_description: this.form.client.activity_description,
            },
            address: {
              city: this.form.address.city,
              district: this.form.address.district,
              street: this.form.address.street,
              building_number: this.form.address.building,
              additional_number: this.form.address.additional,
              postal_code: this.form.address.postalCode,
              short_address: this.form.address.shortAddress,
            },
            bank: {
              account_holder_name: this.form.bank.holder,
              account_number: this.form.bank.iban,
              bank_name: this.form.bank.name,
            },
          };

          console.log("➡️ UPDATE payload:", payload);

          res = await api.post(url, payload, {
            headers: { Accept: "application/json" },
          });
        }

        console.log("✅ UPDATE response:", res?.data);

        const updatedClient = res?.data?.data || res?.data?.user || res?.data;

        if (this.isConsultant) this.setConsultantUser(updatedClient);
        else this.setClientUser(updatedClient);

        this.fillFormFromUser(true);

        // reset selected files after save
        this.form.attachments = [];

        this.showToast(this.t("account_saved_success"), "success");
      } catch (err) {
        const res = err.response?.data;

        console.error("❌ UPDATE error:", err);
        console.error("❌ UPDATE response:", res);

        if (res?.errors) {
          this.errors = res.errors;
          const errorList = Object.values(res.errors)
            .flat()
            .filter(Boolean)
            .join("\n");
          this.showToast(errorList, "error");
        } else {
          this.showToast(
            res?.message || this.t("account_saved_failed"),
            "error"
          );
        }
      } finally {
        this.saving = false;
      }
    },

    // ---------------------------
    // ✅ HYDRATE LIKE OLD TEMPLATE
    // ---------------------------
    fillFormFromUser(force = false) {
      if (this.hydratedOnce && !force) return;

      this.$nextTick(() => {
        if (!this.user) return;

        const src = this.isConsultant ? this.user.consultant : this.user.client;

        // MAIN (client/consultant)
        this.form.client.name = src?.name ?? "";
        this.form.client.activity = this.isConsultant
          ? src?.specialization ?? ""
          : src?.activity ?? "";
        this.form.client.manager = this.isConsultant
          ? src?.license_number ?? ""
          : src?.manager_name ?? "";

        this.form.client.activity_description = this.isConsultant
          ? ""
          : src?.activity_description ?? "";

        // ADDRESS
        this.form.address.city = this.user?.address?.city ?? "";
        this.form.address.district = this.user?.address?.district ?? "";
        this.form.address.street = this.user?.address?.street ?? "";
        this.form.address.building = this.user?.address?.building_number ?? "";
        this.form.address.additional =
          this.user?.address?.additional_number ?? "";
        this.form.address.postalCode = this.user?.address?.postal_code ?? "";
        this.form.address.shortAddress =
          this.user?.address?.short_address ?? "";

        // BANK
        this.form.bank.holder =
          this.user?.bank_account?.account_holder_name ?? "";
        this.form.bank.iban = this.user?.bank_account?.account_number ?? "";
        this.form.bank.name = this.user?.bank_account?.bank_name ?? "";

        this.hydratedOnce = true;
      });
    },

    // ---------------------------
    // ✅ CHANGE PASSWORD
    // ---------------------------
    async savePassword() {
      if (this.passwordSaving || this.loading) return;

      this.passwordSaving = true;

      // reset errors
      this.passwordErrors = {
        current_password: {},
        password: {},
        password_confirmation: {},
      };

      try {
        // ✅ نفس endpoint اللي عطيتني
        const url = "/users/updatePassword";

        const bodyData = {
          old_password: this.passwordForm.current_password,
          password: this.passwordForm.password,
          password_confirmation: this.passwordForm.password_confirmation,
        };

        const { data: res } = await api.patch(url, bodyData, {
          headers: { Accept: "application/json" },
        });

        this.showToast(
          res?.message || this.t("security_password_success"),
          "success"
        );

        // reset fields
        this.passwordForm.current_password = "";
        this.passwordForm.password = "";
        this.passwordForm.password_confirmation = "";

        // reset eye toggles if you have them
        if (this.passwordVisible) {
          this.passwordVisible.current = false;
          this.passwordVisible.new = false;
          this.passwordVisible.confirm = false;
        }
      } catch (err) {
        const res = err.response?.data;

        if (res?.errors) {
          this.passwordErrors = res.errors;

          const errorList = Object.values(res.errors)
            .flat()
            .filter(Boolean)
            .join("\n");

          this.showToast(errorList, "error");
        } else {
          this.showToast(
            res?.message || this.t("security_password_failed"),
            "error"
          );
        }
      } finally {
        this.passwordSaving = false;
      }
    },

    // ---------------------------
    // Toast helper
    // ---------------------------
    showToast(message, type = "success") {
      this.toast.message = translateCookieToastMessage(message, this.t.bind(this));
      this.toast.type = type;
      this.toast.show = true;

      if (this._toastTimer) clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.toast.show = false;
      }, 2500);
    },

    // ---------------------------
    // Language Sync
    // ---------------------------
    syncLangListeners() {
      this._langHandler = (e) => {
        const next = (e.detail || "ar").toLowerCase().startsWith("en")
          ? "en"
          : "ar";
        if (next !== this.lang) this.lang = next;
      };
      window.addEventListener("app:lang", this._langHandler, { passive: true });

      this._langObserver = new MutationObserver(() => {
        const htmlLang = (
          document.documentElement.lang ||
          localStorage.getItem("app_lang") ||
          "ar"
        ).toLowerCase();
        const next = htmlLang.startsWith("en") ? "en" : "ar";
        if (next !== this.lang) this.lang = next;
      });

      this._langObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["lang", "dir"],
      });
    },

    // Cookie helper (ID fallback only)
    getCookie(name) {
      const match = document.cookie.match(
        new RegExp(
          "(?:^|; )" +
            name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") +
            "=([^;]*)"
        )
      );
      return match ? decodeURIComponent(match[1]) : null;
    },
  },
};
</script>
